import { createContext, useCallback, useState } from 'react';
import { useReporteService } from 'core/services/useCoreService';
import { UltimasLiquidacionResumen } from 'core/reporte/models/UltimasLiquidacionResumen';
import { useNotifications } from 'shared/notifications';
import { useFileDownloader } from 'shared/hooks/useFileDownloader';

export interface IUltimasLiquidacionesContext {
  resumenes: UltimasLiquidacionResumen[];
  showResults: boolean;
  numeroCuenta: number | null;
  isLoading: boolean;
  isTableLoading: boolean;
  downloadingKeyValue: string | null;
  isBulkDownloading: boolean;
  selectedKeyValues: Set<string>;
  selectionInvalid: boolean;
  consultar: (numeroCuenta: number) => Promise<void>;
  clear: () => void;
  toggleSelection: (keyValue: string, selected: boolean) => void;
  downloadResumen: (resumen: UltimasLiquidacionResumen) => Promise<void>;
  downloadSelected: () => Promise<void>;
}

export const UltimasLiquidacionesContext = createContext<IUltimasLiquidacionesContext>({
  resumenes: [],
  showResults: false,
  numeroCuenta: null,
  isLoading: false,
  isTableLoading: false,
  downloadingKeyValue: null,
  isBulkDownloading: false,
  selectedKeyValues: new Set(),
  selectionInvalid: false,
  consultar: async () => {},
  clear: () => {},
  toggleSelection: () => {},
  downloadResumen: async () => {},
  downloadSelected: async () => {},
});

export const UltimasLiquidacionesProvider = ({ children }) => {
  const {
    listarUltimasLiquidaciones,
    descargarUltimaLiquidacionPdf,
    descargarUltimasLiquidacionesZip,
  } = useReporteService();
  const { addNotification } = useNotifications();
  const { downloadFile } = useFileDownloader();

  const [resumenes, setResumenes] = useState<UltimasLiquidacionResumen[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [downloadingKeyValue, setDownloadingKeyValue] = useState<string | null>(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [selectedKeyValues, setSelectedKeyValues] = useState<Set<string>>(new Set());
  const [selectionInvalid, setSelectionInvalid] = useState(false);
  const [numeroCuenta, setNumeroCuenta] = useState<number | null>(null);

  const clearSelection = useCallback(() => {
    setSelectedKeyValues(new Set());
    setSelectionInvalid(false);
  }, []);

  const consultar = useCallback(
    async (cuenta: number) => {
      setNumeroCuenta(cuenta);
      setIsLoading(true);
      setIsTableLoading(true);
      setShowResults(true);
      clearSelection();

      try {
        const result = await listarUltimasLiquidaciones(cuenta);
        setResumenes(result);
      } catch (error) {
        console.error('Error al consultar ultimas liquidaciones:', error);
        setResumenes([]);
        addNotification('No se pudo obtener el listado de resumenes.', 'error');
      } finally {
        setIsLoading(false);
        setIsTableLoading(false);
      }
    },
    [listarUltimasLiquidaciones, addNotification, clearSelection],
  );

  const clear = useCallback(() => {
    setResumenes([]);
    setShowResults(false);
    setNumeroCuenta(null);
    clearSelection();
  }, [clearSelection]);

  const toggleSelection = useCallback((keyValue: string, selected: boolean) => {
    setSelectedKeyValues((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(keyValue);
      } else {
        next.delete(keyValue);
      }
      if (next.size > 0) {
        setSelectionInvalid(false);
      }
      return next;
    });
  }, []);

  const downloadResumen = useCallback(
    async (resumen: UltimasLiquidacionResumen) => {
      if (!numeroCuenta) {
        return;
      }

      setDownloadingKeyValue(resumen.keyValue);

      try {
        const fileData = await descargarUltimaLiquidacionPdf(numeroCuenta, resumen.keyValue);
        const fileName =
          fileData.name ||
          `resumen-liquidacion-${resumen.accountNumber}-${resumen.closingDate || resumen.id}.pdf`;
        downloadFile(fileData, fileName);
      } catch (error) {
        console.error('Error al descargar resumen:', error);
        addNotification('No se pudo obtener el PDF.', 'error');
      } finally {
        setDownloadingKeyValue(null);
      }
    },
    [numeroCuenta, descargarUltimaLiquidacionPdf, downloadFile, addNotification],
  );

  const downloadSelected = useCallback(async () => {
    if (!numeroCuenta) {
      return;
    }

    if (selectedKeyValues.size === 0) {
      setSelectionInvalid(true);
      return;
    }

    setSelectionInvalid(false);
    setIsBulkDownloading(true);

    try {
      const fileData = await descargarUltimasLiquidacionesZip(
        numeroCuenta,
        Array.from(selectedKeyValues),
      );
      const fileName = fileData.name || `liquidaciones-${numeroCuenta}.zip`;
      downloadFile(fileData, fileName);
    } catch (error) {
      console.error('Error al descargar liquidaciones seleccionadas:', error);
      addNotification('No se pudo obtener el archivo ZIP.', 'error');
    } finally {
      setIsBulkDownloading(false);
    }
  }, [
    numeroCuenta,
    selectedKeyValues,
    descargarUltimasLiquidacionesZip,
    downloadFile,
    addNotification,
  ]);

  const value: IUltimasLiquidacionesContext = {
    resumenes,
    showResults,
    numeroCuenta,
    isLoading,
    isTableLoading,
    downloadingKeyValue,
    isBulkDownloading,
    selectedKeyValues,
    selectionInvalid,
    consultar,
    clear,
    toggleSelection,
    downloadResumen,
    downloadSelected,
  };

  return (
    <UltimasLiquidacionesContext.Provider value={value}>
      {children}
    </UltimasLiquidacionesContext.Provider>
  );
};
